import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, Input } from '@repo/ui';
import { useInvoiceSearch } from '@xtnd/web/features/ar-collections/queries/invoiceQuery';
import { GroupByOption, CollectionSortByOption, SortOrder } from '@repo/types';
import queryString from 'query-string';
import { InvoiceSearchItem } from '@xtnd/web/features/ar-collections/api/invoiceApi';

export function QuickSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState('');
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Debounce searchValue to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchValue]);

  // Fetch recent matters (top 5, sorted by updatedAt desc)
  const { data: recentMattersData, isLoading: isLoadingRecent } = useInvoiceSearch(
    { limit: 5 },
    {
      enabled: isOpen && !debouncedSearchValue.trim(), // Only fetch when dropdown is open and no search input
    }
  );

  // Fetch search results when user types invoice number
  const { data: searchResultsData, isLoading: isLoadingSearch } = useInvoiceSearch(
    {
      invoiceNumber: debouncedSearchValue.trim(),
    },
    {
      enabled: isOpen && debouncedSearchValue.trim().length > 0, // Only fetch when there's search input
    }
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchValue('');
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle search submission
  const handleSearch = (invoice?: InvoiceSearchItem) => {
    // Use matterNumber from dropdown click
    if (!invoice?.matterDisplayNumber?.trim()) return;
    const searchTerm = invoice?.matterDisplayNumber.trim();

    // Navigate to collections page with filters
    const filters = {
      search: searchTerm,
      groupBy: GroupByOption.NONE,
      daysPastDue: invoice?.dayPastDue > 0 ? 1 : 0,
    };
    const sorts = {
      [CollectionSortByOption.DAYS_PAST_DUE]: SortOrder.DESC,
    };

    const queryParams = queryString.stringify({
      filters: JSON.stringify(filters),
      sorts: JSON.stringify(sorts),
      limit: 50,
      page: 1,
    });

    navigate(`/ar-collections/queue?${queryParams}`);
    setIsOpen(false);
    setSearchValue('');
  };

  // Handle Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchValue('');
    }
  };

  const recentMatters = recentMattersData?.data || [];
  const searchResults = searchResultsData?.data || [];
  const hasSearchInput = searchValue.trim().length > 0;
  const showDropdown =
    isOpen &&
    (recentMatters.length > 0 ||
      searchResults.length > 0 ||
      hasSearchInput ||
      isLoadingRecent ||
      isLoadingSearch);

  // Helper function to format display text
  const formatDisplayText = (matter: (typeof recentMatters)[0]) => {
    const invoiceNumber = matter.invoiceNumber || '';
    const description = matter.matterDisplayNumber
      ? matter.matterDisplayNumber.split('-').slice(1).join('-')
      : '';
    return invoiceNumber && description
      ? `${invoiceNumber} - ${description}`
      : description || invoiceNumber;
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-2xl">
      {/* Search Input */}
      <div className="relative">
        <Input
          type="text"
          placeholder="Search by invoice or matter number..."
          value={searchValue}
          onChange={e => {
            setSearchValue(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          fullWidth={true}
          className="pl-10 pr-10"
        />
        <Icon
          name="HiSearch"
          size="sm"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-xtnd-dark-400 dark:text-xtnd-light"
        />
        {searchValue && (
          <button
            onClick={() => {
              setSearchValue('');
              setIsOpen(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-xtnd-dark-400 hover:text-xtnd-dark dark:text-xtnd-light dark:hover:text-xtnd-white"
          >
            <Icon name="HiX" size="sm" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-xtnd-white dark:bg-xtnd-dark rounded-xl shadow-xl border border-xtnd-light dark:border-xtnd-dark-700 z-50 max-h-80 overflow-y-auto">
          {!hasSearchInput && (isLoadingRecent || recentMatters.length > 0) && (
            <div className="p-2">
              <div className="px-3 py-2 flex items-center gap-2 text-sm font-semibold text-xtnd-dark-400 dark:text-xtnd-light uppercase">
                <Icon name="HiClock" size="sm" className="text-xtnd-blue" />
                Recent Invoices
              </div>
              {isLoadingRecent ? (
                <div className="px-3 py-4 flex items-center justify-center">
                  <Icon name="HiRefresh" className="h-5 w-5 animate-spin text-xtnd-blue" />
                </div>
              ) : recentMatters.length > 0 ? (
                recentMatters.map(matter => {
                  const displayText = formatDisplayText(matter);

                  return (
                    <button
                      key={matter.matterId}
                      onClick={() => handleSearch(matter)}
                      className="w-full px-3 py-2.5 text-left text-sm text-xtnd-dark dark:text-xtnd-white hover:bg-xtnd-light dark:hover:bg-xtnd-dark-700 rounded-lg transition-colors"
                    >
                      <div className="font-medium truncate">{displayText}</div>
                    </button>
                  );
                })
              ) : null}
            </div>
          )}

          {hasSearchInput && (
            <div className="p-2">
              <div className="px-3 py-2 flex items-center gap-2 text-sm font-semibold text-xtnd-dark-400 dark:text-xtnd-light uppercase">
                <Icon name="HiSearch" size="sm" className="text-xtnd-blue" />
                Search Results
              </div>
              {isLoadingSearch ? (
                <div className="px-3 py-4 flex items-center justify-center">
                  <Icon name="HiRefresh" className="h-5 w-5 animate-spin text-xtnd-blue" />
                </div>
              ) : searchResults.length > 0 ? (
                searchResults.map(matter => {
                  const displayText = formatDisplayText(matter);

                  return (
                    <button
                      key={matter.matterId}
                      onClick={() => handleSearch(matter)}
                      className="w-full px-3 py-2.5 text-left text-sm text-xtnd-dark dark:text-xtnd-white hover:bg-xtnd-light dark:hover:bg-xtnd-dark-700 rounded-lg transition-colors"
                    >
                      <div className="font-medium truncate">{displayText}</div>
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-sm text-xtnd-dark-400 dark:text-xtnd-light text-center">
                  No results found
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
